/* eslint-disable no-underscore-dangle */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Meteor } from 'meteor/meteor';
import { get, isEqual, isEmpty } from 'lodash';
import turf from '@turf/turf';

import * as annotationOps from '../../../api/Annotations/annotation-operations';
import * as videoOps from '../../../api/Videos/video-operations';

import { featurePoint2LatLng, featureGetZoom } from '../../../modules/geojson-utilities';

import LeafletMap from '../../components/LeafletMap';

let addOnlineBaseMap;
let drawCustomButtons;
let drawRPAPosition;
let drawVideoAnnotations;
let drawVideoBox;
let drawVideoLines;
let drawVideoTrack;
let drawMapLayers;

class MapContainerViewVideo extends Component {
  state = {
    clientModulesReady: false,
    isLeafletMapLoaded: get(this, 'videoMapComponent.current.state.isMapLoaded', false),
    isVideoMapLoaded: false
  };

  annotationsLayer = null;

  linesLayer = null;

  rpaPositionLayer = null;

  trackLayer = null;

  videoBoxLayer = null;

  videoMapComponent = React.createRef();

  videoMap = null;

  componentDidMount() {
    const { clientModulesReady } = this.state;
    if (!clientModulesReady) {
      this.loadClientModules();
    }
  }

  componentDidUpdate(prevProps) {
    const {
      video,
      annotations,
      currentFeature,
      currentRPAPosition,
      lines,
      rpaBearing,
      showVideoBox,
      updateContinuouslyMapLocation,
      updateMapLocation,
      videoBox,
      changeVideoContainerStateProperty
    } = this.props;
    const { clientModulesReady, isLeafletMapLoaded, isVideoMapLoaded } = this.state;
    const {
      video: prevVideo,
      annotations: prevAnnotations,
      currentRPAPosition: prevCurrentRPAPosition,
      lines: prevLines,
      rpaBearing: prevRPABearing,
      showVideoBox: prevShowVideoBox,
      videoBox: prevVideoBox
    } = prevProps;
    if (clientModulesReady && isLeafletMapLoaded && !isVideoMapLoaded) {
      this.initializeMap();
    }

    if (isVideoMapLoaded && (updateContinuouslyMapLocation || updateMapLocation)) {
      this.videoMap.setView(featurePoint2LatLng(currentFeature));
      if (updateMapLocation) changeVideoContainerStateProperty('updateMapLocation', false);
    }

    if (
      !isEqual(currentRPAPosition, prevCurrentRPAPosition) ||
      !isEqual(rpaBearing, prevRPABearing)
    ) {
      drawRPAPosition({
        leafletMap: this.videoMap,
        currentRPAPosition,
        rpaBearing,
        RPAPositionLayer: this.rpaPositionLayer,
        isUpdate: true
      });
    }

    if (
      isVideoMapLoaded &&
      (!isEqual(videoBox, prevVideoBox) || !isEqual(showVideoBox, prevShowVideoBox))
    ) {
      drawVideoBox({
        leafletMap: this.videoMap,
        videoBox,
        videoBoxLayer: this.videoBoxLayer,
        showVideoBox,
        isUpdate: true
      });
    }

    if (isVideoMapLoaded && !isEqual(lines, prevLines)) {
      drawVideoLines({
        leafletMap: this.videoMap,
        lines,
        linesLayer: this.linesLayer,
        isUpdate: true
      });
    }

    if (isVideoMapLoaded && !isEqual(annotations, prevAnnotations)) {
      drawVideoAnnotations({
        leafletMap: this.videoMap,
        annotations,
        onClickFunction: annotation => {
          changeVideoContainerStateProperty('updateVideo', true);
          changeVideoContainerStateProperty('videoTime', annotationOps.getVideoTime(annotation));
          changeVideoContainerStateProperty('selectedAnnotation', annotation);
        },
        annotationsLayer: this.annotationsLayer,
        isUpdate: true
      });
    }

    if (!isEqual(videoOps.isShared(video), videoOps.isShared(prevVideo))) {
      drawCustomButtons({
        map: {
          selectedButtons: {
            Attribution: true,
            Home: true,
            QRCode: true,
            Scale: true,
            Search: false,
            Title: true,
            Zoom: true
          },
          mapLocation: turf.point(videoOps.getLatLng0(video), { zoom: 16 }),
          name: videoOps.getName(video),
          publicId: videoOps.getId(video),
          shared: videoOps.isShared(video),
          sharedPrivate: videoOps.isSharedPrivate(video)
        },
        type: 'video',
        leafletMap: this.videoMap
      });
    }
  }

  updateMapState = newState => {
    this.setState({ isLeafletMapLoaded: newState });
  };

  initializeMap = async () => {
    const {
      video,
      annotations,
      currentRPAPosition,
      lines,
      rpaBearing,
      showVideoBox,
      videoBox,
      changeVideoContainerStateProperty,
      fetchTime,
      initializeLoop,
      setReferenceInVideoContainer,
      updateVideoStyle
    } = this.props;

    const location = turf.point(videoOps.getLatLng0(video), { zoom: 16 });

    this.videoMap = get(this, 'videoMapComponent.current.leafletMap');
    this.videoMap.setView(featurePoint2LatLng(location), featureGetZoom(location));

    const { baseMaps, layersMaps } = videoOps.getGeomotionMap(video);
    const base = !isEmpty(baseMaps) ? baseMaps : [];
    const layer = !isEmpty(layersMaps) ? layersMaps : [];

    if (!isEmpty(baseMaps)) {
      await drawMapLayers({
        layers: layer,
        baseMaps: base,
        popupProp: false,
        leafletMap: this.videoMap,
        layersToDisplay: layer,
        baseMapsToDisplay: base,
        isUpdate: false,
        baseMapsTransparency: [],
        layersTransparency: [],
        baseMapsZoom: [],
        layersZoom: []
      });
    } else addOnlineBaseMap(this.videoMap);

    this.videoMap.on('zoom move', updateVideoStyle);

    if (!videoOps.areCoordinatesFromPoint(video)) {
      drawVideoTrack({
        leafletMap: this.videoMap,
        track: videoOps.getTrack(video),
        onClickFunction: fetchTime
      });
    }

    this.rpaPositionLayer = drawRPAPosition({
      leafletMap: this.videoMap,
      currentRPAPosition,
      rpaBearing
    });

    this.videoBoxLayer = drawVideoBox({
      leafletMap: this.videoMap,
      videoBox,
      showVideoBox
    });

    this.linesLayer = drawVideoLines({
      leafletMap: this.videoMap,
      lines
    });

    this.annotationsLayer = drawVideoAnnotations({
      leafletMap: this.videoMap,
      annotations,
      onClickFunction: annotation => {
        changeVideoContainerStateProperty('updateVideo', true);
        changeVideoContainerStateProperty('videoTime', annotationOps.getVideoTime(annotation));
        changeVideoContainerStateProperty('selectedAnnotation', annotation);
      }
    });

    drawCustomButtons({
      map: {
        selectedButtons: {
          Attribution: true,
          Home: true,
          QRCode: true,
          Scale: true,
          Search: false,
          Title: true,
          Zoom: true
        },
        mapLocation: location,
        name: videoOps.getName(video),
        publicId: videoOps.getId(video),
        shared: videoOps.isShared(video)
      },
      type: 'video',
      leafletMap: this.videoMap
    });

    this.setState({ isVideoMapLoaded: true });
    setReferenceInVideoContainer('videoMap', this.videoMap);

    initializeLoop();
  };

  async loadClientModules() {
    if (Meteor.isClient) {
      ({
        addOnlineBaseMap,
        drawCustomButtons,
        drawRPAPosition,
        drawVideoAnnotations,
        drawVideoBox,
        drawVideoLines,
        drawVideoTrack,
        drawMapLayers
      } = await import('../../../modules/leaflet-operations'));
      this.setState({ clientModulesReady: true });
    }
  }

  render() {
    const { deployEnv, setReferenceInVideoContainer } = this.props;

    return (
      <div ref={mapContainer => setReferenceInVideoContainer('mapDiv', mapContainer)}>
        <LeafletMap
          mapId="videoMap"
          headerType="7"
          ref={this.videoMapComponent}
          updateMapState={this.updateMapState}
          deployEnv={deployEnv}
        />
      </div>
    );
  }
}

MapContainerViewVideo.propTypes = {
  video: PropTypes.object.isRequired,
  annotations: PropTypes.array.isRequired,
  currentFeature: PropTypes.object.isRequired,
  currentRPAPosition: PropTypes.object.isRequired,
  deployEnv: PropTypes.string.isRequired,
  lines: PropTypes.array.isRequired,
  rpaBearing: PropTypes.number.isRequired,
  showVideoBox: PropTypes.bool.isRequired,
  updateContinuouslyMapLocation: PropTypes.bool.isRequired,
  updateMapLocation: PropTypes.bool.isRequired,
  videoBox: PropTypes.object.isRequired,
  changeVideoContainerStateProperty: PropTypes.func.isRequired,
  fetchTime: PropTypes.func.isRequired,
  initializeLoop: PropTypes.func.isRequired,
  setReferenceInVideoContainer: PropTypes.func.isRequired,
  updateVideoStyle: PropTypes.func.isRequired
};

export default MapContainerViewVideo;
